import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  ShoppingCart, 
  LogOut,
  TrendingUp,
  DollarSign,
  UserPlus,
  Package,
  ClipboardList
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

function AdminSidebar({ user }) {
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/');
  };
  
  return (
    <aside className="w-64 bg-[#0A0A0A] border-r border-white/10 h-screen fixed left-0 top-0 flex flex-col">
      <div className="p-6 border-b border-white/10">
        <Link to="/" className="text-xl font-bold tracking-tight">
          AG Collective
        </Link>
        <p className="text-xs text-zinc-500 mt-1">Panel Admin</p>
      </div>
      
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          <li>
            <Link 
              to="/admin" 
              className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>
          </li>
          <li>
            <Link 
              to="/admin/leads" 
              className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-leads"
            >
              <Users className="w-5 h-5" />
              Leads
            </Link>
          </li>
          <li>
            <Link 
              to="/admin/orders" 
              className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-orders"
            >
              <ShoppingCart className="w-5 h-5" />
              Órdenes
            </Link>
          </li>
          <li>
            <Link 
              to="/admin/onboarding" 
              className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-onboarding"
            >
              <ClipboardList className="w-5 h-5" />
              Onboarding
            </Link>
          </li>
        </ul>
      </nav>
      
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          {user?.picture && (
            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-zinc-400 hover:text-white hover:bg-white/5"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Cerrar Sesión
        </Button>
      </div>
    </aside>
  );
}

function StatCard({ icon: Icon, title, value, subtitle, trend }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/5 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 bg-white/5 flex items-center justify-center">
          <Icon className="w-5 h-5 text-zinc-400" />
        </div>
        {trend && (
          <span className={`text-xs font-bold ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold mb-1">{value}</p>
      <p className="text-sm text-zinc-500">{title}</p>
      {subtitle && <p className="text-xs text-zinc-600 mt-1">{subtitle}</p>}
    </div>
  );
}

export default function AdminDashboard({ user }) {
  const [stats, setStats] = useState({
    total_leads: 0,
    new_leads: 0,
    total_orders: 0,
    completed_orders: 0,
    total_revenue: 0,
    total_onboarding: 0,
    pending_onboarding: 0
  });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/api/stats`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <AdminSidebar user={user} />
      
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-zinc-500 mt-1">Bienvenido, {user?.name}</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard 
                icon={Users} 
                title="Total Leads" 
                value={stats.total_leads}
                subtitle={`${stats.new_leads} nuevos`}
              />
              <StatCard 
                icon={UserPlus} 
                title="Leads Nuevos" 
                value={stats.new_leads}
              />
              <StatCard 
                icon={Package} 
                title="Órdenes Totales" 
                value={stats.total_orders}
                subtitle={`${stats.completed_orders} completadas`}
              />
              <StatCard 
                icon={DollarSign} 
                title="Ingresos Totales" 
                value={`$${stats.total_revenue.toLocaleString()}`}
                subtitle="USD"
              />
            </div>
            
            {/* Onboarding Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <StatCard 
                icon={ClipboardList} 
                title="Total Onboarding" 
                value={stats.total_onboarding}
                subtitle={`${stats.pending_onboarding} pendientes`}
              />
              <StatCard 
                icon={TrendingUp} 
                title="Proyectos Activos" 
                value={stats.pending_onboarding}
                subtitle="En proceso"
              />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#0A0A0A] border border-white/5 p-6">
                <h2 className="text-xl font-bold mb-4">Acciones Rápidas</h2>
                <div className="space-y-4">
                  <Link to="/admin/leads">
                    <Button className="w-full justify-start bg-white/5 hover:bg-white/10 text-white">
                      <Users className="w-5 h-5 mr-3" />
                      Ver todos los leads
                    </Button>
                  </Link>
                  <Link to="/admin/orders">
                    <Button className="w-full justify-start bg-white/5 hover:bg-white/10 text-white">
                      <ShoppingCart className="w-5 h-5 mr-3" />
                      Ver todas las órdenes
                    </Button>
                  </Link>
                  <Link to="/admin/onboarding">
                    <Button className="w-full justify-start bg-white/5 hover:bg-white/10 text-white">
                      <ClipboardList className="w-5 h-5 mr-3" />
                      Ver proyectos onboarding
                    </Button>
                  </Link>
                </div>
              </div>
              
              <div className="bg-[#0A0A0A] border border-white/5 p-6">
                <h2 className="text-xl font-bold mb-4">Resumen</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <span className="text-zinc-400">Tasa de conversión</span>
                    <span className="font-bold">
                      {stats.total_leads > 0 
                        ? ((stats.completed_orders / stats.total_leads) * 100).toFixed(1) 
                        : 0}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <span className="text-zinc-400">Ticket promedio</span>
                    <span className="font-bold">
                      ${stats.completed_orders > 0 
                        ? (stats.total_revenue / stats.completed_orders).toFixed(0) 
                        : 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-zinc-400">Órdenes pendientes</span>
                    <span className="font-bold text-yellow-500">
                      {stats.total_orders - stats.completed_orders}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export { AdminSidebar };
